import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { createOperationId } from '../../domain/operationId';
import type {
  CollectionTag,
  ManagementSharingAction,
  ManagementSharingRequestMap,
  ManagementSharingResultMap,
  SharingCommandResult,
  SharedItem as SharedItemDto,
  SharedViewAction,
  SharedViewRequestMap,
  SharedViewResultMap,
  SharedViewScope,
} from '@qkindex/contracts';

export type ShareTag = Pick<
  CollectionTag,
  'tagId' | 'name' | 'state' | 'revision'
>;
export type SharedItem = SharedItemDto & { image?: string };
export type SharingResult = Partial<
  ManagementSharingResultMap['list'] & SharingCommandResult
>;
const manage = httpsCallable<Record<string, unknown>, unknown>(
  functions,
  'manageCollectionSharing',
);
const recipient = httpsCallable<Record<string, unknown>, unknown>(
  functions,
  'sharedView',
);
// Retain operation identity across transport retries of the same intent.
const intents = new Map<string, string>();
export async function manageSharing<A extends ManagementSharingAction>(
  collectionId: string,
  action: A,
  fields: ManagementSharingRequestMap[A] = {} as ManagementSharingRequestMap[A],
): Promise<ManagementSharingResultMap[A]> {
  const key = JSON.stringify([collectionId, action, fields]);
  const operationId = intents.get(key) || createOperationId();
  intents.set(key, operationId);
  const result = (
    await manage({ collectionId, action, operationId, ...fields })
  ).data;
  intents.delete(key);
  if (['saveTag', 'deleteTag'].includes(action))
    window.dispatchEvent(
      new CustomEvent('qkindex.collectionTagsChanged', {
        detail: collectionId,
      }),
    );
  return result as ManagementSharingResultMap[A];
}
export async function readShared<A extends SharedViewAction>(
  action: A,
  fields: SharedViewRequestMap[A],
): Promise<SharedViewResultMap[A]> {
  return (await recipient({ action, ...fields }))
    .data as SharedViewResultMap[A];
}
export function sharedPath(
  collectionId: string,
  shareId: string,
  token?: string,
  invitation = false,
) {
  const path = `/shared/${encodeURIComponent(collectionId)}/${encodeURIComponent(shareId)}`;
  return token
    ? `${path}#${invitation ? 'invitation' : 'link'}=${encodeURIComponent(token)}`
    : path;
}
export async function sharedLocations(scope: SharedViewScope) {
  const locations: Array<{ id: string; name: string }> = [];
  let cursor: string | undefined;
  do {
    const page = await readShared('locations', {
      ...scope,
      ...(cursor ? { cursor } : {}),
    });
    locations.push(...page.locations);
    cursor = page.nextCursor;
  } while (cursor);
  return locations;
}
